import { ActionIcon, Badge, Group, HoverCard, Stack, Text } from '@mantine/core'
import { IconCertificate } from '@tabler/icons-react'
import {
  formatProfessionalRole,
  PRACTITIONER_QUALIFICATIONS,
  SPECIALTIES_BY_QUALIFICATION,
} from '@behuman/core-contract'
import { states } from '@/utils/states'
import type { PractitionerRow } from '@/hooks'

// Code → label lookups derived from the contract so this card can never drift
// from the credentials editor's option labels (the single source of truth).
const QUALIFICATION_LABELS: Record<string, string> = Object.fromEntries(
  PRACTITIONER_QUALIFICATIONS.map((q) => [q.value, q.label]),
)

const SPECIALTY_LABELS: Record<string, string> = Object.fromEntries(
  Object.values(SPECIALTIES_BY_QUALIFICATION)
    .flat()
    .map((s) => [s.value, s.label]),
)

const NOT_AVAILABLE = 'N/A'

function stateLabel(value: string): string {
  return states.find((s) => s.value === value)?.label ?? value
}

type Props = {
  member: PractitionerRow
}

export function MemberQualificationsHoverCard({ member }: Props) {
  const { professionalRole, specialty } = member
  return (
    <HoverCard shadow="md" withArrow position="right" openDelay={120}>
      <HoverCard.Target>
        <ActionIcon variant="subtle" color="gray" size="sm" aria-label="View qualifications">
          <IconCertificate size={16} />
        </ActionIcon>
      </HoverCard.Target>
      <HoverCard.Dropdown>
        <Stack gap="xs">
          <Group gap="lg" justify="space-between" wrap="nowrap">
            <Text size="sm" c="dimmed">
              NPI Number
            </Text>
            <Text size="sm" fw={500} ta="right" style={{ whiteSpace: 'nowrap' }}>
              {member.npiNumber || NOT_AVAILABLE}
            </Text>
          </Group>

          <Group gap="lg" justify="space-between" wrap="nowrap">
            <Text size="sm" c="dimmed">
              Professional role
            </Text>
            {/* The codes as the Provider Operations list prints them; the long labels ride
                on the tooltip. */}
            <Text
              size="sm"
              fw={500}
              ta="right"
              title={
                professionalRole
                  ? [
                      QUALIFICATION_LABELS[professionalRole],
                      specialty && SPECIALTY_LABELS[specialty],
                    ]
                      .filter(Boolean)
                      .join(' · ')
                  : undefined
              }
            >
              {formatProfessionalRole(professionalRole, specialty) || NOT_AVAILABLE}
            </Text>
          </Group>

          <Stack gap={4}>
            <Text size="sm" c="dimmed">
              Licenses
            </Text>
            {member.licenses.length > 0 ? (
              <Group gap={6}>
                {member.licenses.map((license) => (
                  <Badge key={license.state} variant="light" title={stateLabel(license.state)}>
                    {license.state}
                  </Badge>
                ))}
              </Group>
            ) : (
              <Text size="sm" fw={500}>
                {NOT_AVAILABLE}
              </Text>
            )}
          </Stack>
        </Stack>
      </HoverCard.Dropdown>
    </HoverCard>
  )
}
